import * as Sentry from '@sentry/nuxt'
import { io, type Socket } from 'socket.io-client'

import { INITIAL_EXPECTED_CONNECT_ERROR_REPORT_THRESHOLD } from './constants'
import { getIsExpectedConnectError } from './helpers'

interface Listener {
  action: string
  callback: (...args: any[]) => any
}

/** @see https://socket.io/docs/v4/client-options */
export class WSS {
  host: string
  path: string

  connection: Socket | null = null
  connected = false
  onceLostConnection = false
  readyState: Promise<void> | null = null
  listeners: Listener[] = []
  mapChannelSubscribersCount: Map<string, number> = new Map()
  authorized = false
  getAuthorizationToken: () => Promise<string | null> = async () => null

  constructor(
    endpoint: string,
    public namespace = '/'
  ) {
    const url = new URL(endpoint)

    this.host = url.origin
    this.path = url.pathname
  }

  connect() {
    this.readyState = new Promise<void>(resolve => {
      this.connection = io(this.host + this.namespace, {
        path: this.path,
        autoConnect: true,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 10000,
        transports: ['websocket']
      })

      const wssContext = {
        host: this.host,
        path: this.path,
        namespace: this.namespace
      }

      let initialConnectErrorsCount = 0
      let isConnectErrorReported = false

      this.connection.once('connect', () => resolve())

      this.connection.on('connect_error', (err: Error) => {
        Sentry.addBreadcrumb({
          category: 'wss',
          message: 'connect_error',
          level: 'warning',
          data: {
            ...wssContext,
            error: err.message,
            visibilityState: document.visibilityState,
            onLine: navigator.onLine
          }
        })

        const isExpectedConnectError = getIsExpectedConnectError(err.message)
        const isInitialConnect = !this.connected && !this.onceLostConnection

        if (isExpectedConnectError && !isInitialConnect) {
          return
        }

        if (isExpectedConnectError) {
          initialConnectErrorsCount++

          /**
           * Ошибку из списка ожидаемых отправляем в Sentry ТОЛЬКО если не удаётся установить ПЕРВОЕ соединение
           * спустя INITIAL_EXPECTED_CONNECT_ERROR_REPORT_THRESHOLD попыток
           */
          const shouldReportInitialFailure =
            !isConnectErrorReported &&
            initialConnectErrorsCount >=
              INITIAL_EXPECTED_CONNECT_ERROR_REPORT_THRESHOLD

          if (!shouldReportInitialFailure) {
            return
          }

          /**
           * Гарантия, что в рамках сессии отправится только один репорт для нештатных ошибок
           */
        } else if (isConnectErrorReported) {
          return
        }

        Sentry.captureException(err, {
          tags: { source: 'wss' },
          extra: {
            message: isInitialConnect
              ? 'Ошибка при первом подключении WebSocket'
              : 'Ошибка при переподключении к WebSocket',
            ...wssContext
          }
        })

        isConnectErrorReported = true
      })

      this.connection.on('connect', async () => {
        initialConnectErrorsCount = 0
        isConnectErrorReported = false
        this.connected = true
        this.onAction('connection', this.connected)

        Sentry.addBreadcrumb({
          category: 'wss',
          message: 'connect',
          data: wssContext
        })

        if (this.authorized) {
          await this.authorize()
        }

        this.mapChannelSubscribersCount.forEach((_count, channel) => {
          this.emit('subscribe', { channel })
        })
      })

      this.connection.once('disconnect', () => {
        this.onceLostConnection = true
      })

      this.connection.on('disconnect', (reason: string) => {
        this.connected = false
        this.onAction('connection', this.connected)

        Sentry.addBreadcrumb({
          category: 'wss',
          message: 'disconnect',
          data: { ...wssContext, reason }
        })
      })

      this.connection.on(
        'message',
        ({
          action,
          payload = null
        }: {
          action?: string
          payload?: any
        } = {}) => {
          if (action) {
            this.onAction(action, payload)
          }
        }
      )

      this.connection.onAny((event, payload) => {
        if (event !== 'message') {
          this.onAction(String(event), payload)
        }
      })
    })

    return this
  }

  disconnect() {
    if (!this.connection) {
      return
    }

    this.connection.disconnect()
    this.connection = null
    this.connected = false
    this.onceLostConnection = false
    this.readyState = null
  }

  async emit<T>(action: string, payload: any = null) {
    await this.readyState

    if (!this.connected) {
      return Promise.reject(Error('WebSockets disconnected'))
    }

    return new Promise<T>((resolve, reject) => {
      if (!this.connection) {
        return reject(new Error('No connection'))
      }

      this.connection
        .timeout(10000)
        .emit(action, payload, (err: Error, responseData: any) => {
          if (err || responseData.error) {
            const message = err ? err.message : responseData.message
            return reject(new Error(message))
          }

          resolve(responseData)
        })
    })
  }

  onAction(action: string, payload: any = null) {
    this.listeners.forEach(listener => {
      if (listener.action === action) {
        listener.callback(payload)
      }
    })
  }

  async authorize() {
    this.authorized = true

    if (this.connected) {
      const jwt = await this.getAuthorizationToken()

      if (jwt) {
        await this.emit('authentication', { jwt })
      }
    }
  }

  async logout() {
    if (this.authorized) {
      this.authorized = false
      await this.emit('logout')
    }
  }

  subscribe(channel: string) {
    const subscribersCount = this.mapChannelSubscribersCount.get(channel)

    if (!subscribersCount) {
      this.mapChannelSubscribersCount.set(channel, 1)

      if (this.connected) {
        this.emit('subscribe', { channel })
      }
    } else {
      this.mapChannelSubscribersCount.set(channel, subscribersCount + 1)
    }
  }

  unsubscribe(channel: string) {
    const subscribersCount = this.mapChannelSubscribersCount.get(channel)

    if (subscribersCount === 1) {
      this.mapChannelSubscribersCount.delete(channel)

      if (this.connected) {
        this.emit('unsubscribe', { channel })
      }
    } else if (subscribersCount) {
      this.mapChannelSubscribersCount.set(channel, subscribersCount - 1)
    }
  }

  addEventListener(action: Listener['action'], callback: Listener['callback']) {
    const actions: Listener['action'][] = Array.isArray(action)
      ? action
      : [action]

    actions.forEach(_action =>
      this.listeners.push({ action: _action, callback })
    )

    return this
  }

  removeEventListener(
    action: Listener['action'],
    callback: Listener['callback']
  ) {
    const actions: Listener['action'][] = Array.isArray(action)
      ? action
      : [action]

    actions.forEach(_action => {
      const index = this.listeners.findIndex(
        listener =>
          callback === listener.callback && _action === listener.action
      )

      if (index > -1) {
        this.listeners.splice(index, 1)
      }
    })

    return this
  }
}
